/**
 * PromotionFormFields — Form fields for the Run a Sale modal.
 *
 * Renders adaptive form fields based on the selected promotion type.
 * Uses the config from promotionTypes.ts as single source of truth.
 * Includes conflict detection warning (fires on end date blur).
 *
 * Layout is controlled by the parent (RunASaleModal) — this component
 * renders only the form fields vertically.
 */

import { useCallback } from "react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { getTypeConfig, type PromotionTypeId, type PromotionFormState } from "./promotionTypes";
import { useLanguage } from "@/i18n/useLanguage";
import type { CustomerGroupOption } from "@/features/inventory/api/pricingSummaryHooks";
import type { ConflictingRule } from "@/features/inventory/api/useCheckConflicts";

// ── i18n ────────────────────────────────────────────────────

const i18n = {
  saleName: { en: "Sale name", th: "ชื่อโปรโมชัน" },
  saleNamePlaceholder: { en: "e.g. Spring Sale 2026", th: "เช่น โปรโมชันเดือนเมษายน 2026" },
  allowStacking: { en: "Allow stacking with other promotions", th: "อนุญาตให้ใช้ร่วมกับโปรโมชันอื่น" },
  stackingDesc: { en: "When enabled, this discount can combine with other active promotions", th: "เมื่อเปิดใช้ ส่วนลดนี้สามารถรวมกับโปรโมชันอื่นที่เปิดใช้งานอยู่" },
  minimumQuantity: { en: "Minimum quantity", th: "จำนวนขั้นต่ำ" },
  customerGroup: { en: "Customer group", th: "กลุ่มลูกค้า" },
  appliesTo: { en: "Applies to", th: "ใช้กับ" },
  allCustomers: { en: "All Customers", th: "ลูกค้าทั้งหมด" },
  startDate: { en: "Start date", th: "วันเริ่มต้น" },
  endDate: { en: "End date", th: "วันสิ้นสุด" },
  conflictRunning: { en: "is already running", th: "กำลังใช้งานอยู่" },
  conflictUntil: { en: "until", th: "จนถึง" },
  conflictReplace: { en: "Adding this promotion will replace it for the overlapping period.", th: "การเพิ่มโปรโมชันนี้จะแทนที่ในช่วงเวลาที่ทับซ้อนกัน" },
  replaceIt: { en: "Replace it", th: "แทนที่" },
  changeDates: { en: "Let me change the dates", th: "ขอเปลี่ยนวันที่" },
  // Value labels (used from config, but need Thai equivalents)
  discount: { en: "Discount", th: "ส่วนลด" },
  salePrice: { en: "Sale price", th: "ราคาลด" },
  theirPrice: { en: "Their price", th: "ราคาของกลุ่ม" },
} as const;

// Value label mapping from config valueLabel to i18n key
const VALUE_LABEL_MAP: Record<string, keyof typeof i18n> = {
  "Discount": "discount",
  "Sale price": "salePrice",
  "Their price": "theirPrice",
};

// ── Props ───────────────────────────────────────────────────

interface PromotionFormFieldsProps {
  typeId: PromotionTypeId;
  form: PromotionFormState;
  onChange: (field: keyof PromotionFormState, value: string) => void;
  onToggleStacking: () => void;
  conflicts: ConflictingRule[];
  onCheckConflicts: () => void;
  customerGroups: CustomerGroupOption[];
  conflictResolution: "replace" | "change_dates" | null;
  onConflictResolution: (action: "replace" | "change_dates") => void;
}

const inputClass = cn(
  "w-full min-h-[44px] px-3 py-2 rounded border border-border/40",
  "text-sm font-medium tabular-nums bg-background",
  "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
  "placeholder:text-muted-foreground/40"
);

// ── Component ───────────────────────────────────────────────

export function PromotionFormFields({
  typeId,
  form,
  onChange,
  onToggleStacking,
  conflicts,
  onCheckConflicts,
  customerGroups,
  conflictResolution,
  onConflictResolution,
}: PromotionFormFieldsProps) {
  const { language } = useLanguage();
  const t = (key: keyof typeof i18n) => i18n[key][language] || i18n[key].en;

  const config = getTypeConfig(typeId);

  const handleEndDateBlur = useCallback(() => {
    if (form.validFrom) {
      onCheckConflicts();
    }
  }, [form.validFrom, onCheckConflicts]);

  const valueLabelKey = VALUE_LABEL_MAP[config.valueLabel] || "discount";

  return (
    <div className="space-y-4">
      {/* Conflict warning */}
      {conflicts.length > 0 && (
        <Alert className="border-red-500/30 bg-red-50 dark:bg-red-950/20" role="alert">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-sm text-red-800 dark:text-red-300">
            <p>
              {conflicts[0].title} {t("conflictRunning")}
              {conflicts[0].valid_upto ? ` ${t("conflictUntil")} ${conflicts[0].valid_upto}` : ""}.
              {" "}{t("conflictReplace")}
            </p>
            <div className="flex gap-3 mt-2">
              <button
                type="button"
                onClick={() => onConflictResolution("replace")}
                className={cn(
                  "text-xs font-medium px-2 py-1 rounded-md",
                  conflictResolution === "replace"
                    ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                    : "text-red-600 dark:text-red-400 hover:underline"
                )}
              >
                {t("replaceIt")}
              </button>
              <button
                type="button"
                onClick={() => onConflictResolution("change_dates")}
                className={cn(
                  "text-xs font-medium px-2 py-1 rounded-md",
                  conflictResolution === "change_dates"
                    ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                    : "text-red-600 dark:text-red-400 hover:underline"
                )}
              >
                {t("changeDates")}
              </button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Sale name */}
      <div>
        <label htmlFor="sale-name" className="block text-sm font-medium text-muted-foreground mb-1">
          {t("saleName")}
        </label>
        <input
          id="sale-name"
          type="text"
          value={form.saleName}
          onChange={(e) => onChange("saleName", e.target.value)}
          className={inputClass}
          placeholder={t("saleNamePlaceholder")}
        />
      </div>

      {/* Value field (discount % or sale price) */}
      <div>
        <label htmlFor="promo-value" className="block text-sm font-medium text-muted-foreground mb-1">
          {t(valueLabelKey)}
        </label>
        <div className="relative flex items-center">
          {config.valuePrefix && (
            <span className="absolute left-3 text-sm font-semibold text-muted-foreground select-none" aria-hidden="true">
              {config.valuePrefix}
            </span>
          )}
          <input
            id="promo-value"
            type="text"
            inputMode="decimal"
            value={config.valueField === "discount_percentage" ? form.discountPercentage : form.rate}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d*\.?\d*$/.test(v)) {
                onChange(
                  config.valueField === "discount_percentage" ? "discountPercentage" : "rate",
                  v
                );
              }
            }}
            className={cn(inputClass, config.valuePrefix && "pl-8", config.valueSuffix && "pr-8")}
            placeholder="0"
          />
          {config.valueSuffix && (
            <span className="absolute right-3 text-sm font-semibold text-muted-foreground select-none" aria-hidden="true">
              {config.valueSuffix}
            </span>
          )}
        </div>
      </div>

      {/* Min quantity (quantity deal only) */}
      {config.hasMinQty && (
        <div>
          <label htmlFor="min-qty" className="block text-sm font-medium text-muted-foreground mb-1">
            {t("minimumQuantity")}
          </label>
          <input
            id="min-qty"
            type="text"
            inputMode="numeric"
            value={form.minQty}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d+$/.test(v)) {
                onChange("minQty", v);
              }
            }}
            className={inputClass}
            placeholder="e.g. 5"
          />
        </div>
      )}

      {/* Applies to / Customer group */}
      {(config.hasAppliesTo || typeId === "customer_group_price") && (
        <div>
          <label htmlFor="customer-group" className="block text-sm font-medium text-muted-foreground mb-1">
            {typeId === "customer_group_price" ? t("customerGroup") : t("appliesTo")}
          </label>
          <Select
            value={form.customerGroup}
            onValueChange={(v) => onChange("customerGroup", v)}
          >
            <SelectTrigger id="customer-group" className="min-h-[44px] rounded">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {typeId !== "customer_group_price" && (
                <SelectItem value="All Customers">{t("allCustomers")}</SelectItem>
              )}
              {customerGroups
                .filter((g) => g.name !== "All Customer Groups")
                .map((group) => (
                  <SelectItem key={group.name} value={group.name}>
                    {group.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Start date */}
      <div>
        <label htmlFor="valid-from" className="block text-sm font-medium text-muted-foreground mb-1">
          {t("startDate")}
        </label>
        <input
          id="valid-from"
          type="date"
          value={form.validFrom}
          onChange={(e) => onChange("validFrom", e.target.value)}
          className={inputClass}
        />
      </div>

      {/* End date (not for customer_group_price) */}
      {config.hasEndDate && (
        <div>
          <label htmlFor="valid-upto" className="block text-sm font-medium text-muted-foreground mb-1">
            {t("endDate")}
          </label>
          <input
            id="valid-upto"
            type="date"
            value={form.validUpto}
            onChange={(e) => onChange("validUpto", e.target.value)}
            onBlur={handleEndDateBlur}
            className={inputClass}
          />
        </div>
      )}

      {/* Allow stacking with other promotions */}
      <div className="flex items-start gap-3 pt-2">
        <input
          id="apply-multiple"
          type="checkbox"
          checked={form.applyMultiplePricingRules}
          onChange={onToggleStacking}
          className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
        />
        <div>
          <label htmlFor="apply-multiple" className="block text-sm font-medium cursor-pointer">
            {t("allowStacking")}
          </label>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("stackingDesc")}
          </p>
        </div>
      </div>
    </div>
  );
}
